import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDeleteDialog, DownloadDialog, ErrorDialog, ResourceListPane, ResourceModel, ResourceService } from '@smartsoftware/reflex-core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { Filters, PagedDataSource } from '../../paged.datasource';
import { UserDevicePreferencesService } from '../../services';

@Component({
  selector: 'lib-paged-resource-list',
  templateUrl: './paged-resource-list.component.html',
  styleUrls: ['./paged-resource-list.component.css'],
})
export class PagedResourceListComponent<T extends ResourceModel> implements OnInit, OnDestroy {

  @ViewChild(MatSort) sort?: MatSort;
  @ViewChildren(MatPaginator) paginators?: QueryList<MatPaginator>;
  filterFormGroup?: FormGroup;
  selection = new SelectionModel<T>(true, []);

  private _pagedListDestroy$: Subject<void> = new Subject();
  private _pagedListDelete$: Subject<void> = new Subject();
  private _pagedListDeleteWithConfirm$: Subject<Observable<T>[]> = new Subject();
  private _openError$: Subject<any> = new Subject();

  constructor(
    public dataSource: PagedDataSource<T>,
    protected resourceService: ResourceService<T>,
    protected dialog: MatDialog,
    protected route: ActivatedRoute,
    protected router: Router,
    protected userDevice?: UserDevicePreferencesService
  ) { 
    
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
    this._pagedListDestroy$.next();
    this._pagedListDestroy$.complete();
  }

  ngOnInit(): void {
    this._pagedListDeleteWithConfirm$
      .pipe(
        switchMap((observables: Observable<T>[]) =>
          combineLatest(observables).pipe(
            tap((_) => {
              this.selection.clear();
              this.dataSource.reload();
            })
          )
        ),
        takeUntil(this._pagedListDestroy$)
      )
      .subscribe({ error: (error) => this.openErrorDialog(error) });

    this._pagedListDelete$
      .pipe(
        switchMap(() => this.openConfirmDeleteDialog()),
        takeUntil(this._pagedListDestroy$)
      )
      .subscribe((response) => {
        if (response.confirmed) {
          this.deleteWithConfirm();
        }
      });

    this._openError$
      .pipe(
        switchMap((error) =>
          this.dialog
            .open(ErrorDialog, {
              data: { error: error },
            })
            .afterClosed()
        ),
        takeUntil(this._pagedListDestroy$)
      )
      .subscribe();
  }

  openConfirmDeleteDialog() {
    return this.dialog
        .open(ConfirmDeleteDialog, {
        data: { entities: this.selection.selected }
    })
    .afterClosed();
  }

  openErrorDialog(error:any) {
    this._openError$.next(error);
  }

  public refresh() {
    this.dataSource.reload();
  }

  public download() {
    let entityList = this.dataSource.data;
    this.dialog
        .open(DownloadDialog, {
        data: { entityList }
    });
  }
  public new() {
    this.router.navigate(['./new'], { relativeTo: this.route });
  }
  
  public delete() {
    this._pagedListDelete$.next();
  }

  public deleteWithConfirm() {
    let observables = this.selection.selected.map((entity: T) => {
      entity.isPublished = false;
      entity.isDeleted = true;
      return this.resourceService.push(entity);
    });
    this._pagedListDeleteWithConfirm$.next(observables);
  }

  allSelected() {
    const selected = this.selection.selected.map((e) => e.id);
    if(!this.dataSource.data) return false;
    const data = this.dataSource.data.map((e) => e.id);
    return data.every((value) => selected.includes(value));
  }

  selectAll() {
    const selected = this.selection.selected.map((e) => e.id);
    this.allSelected()
        ? this.dataSource.data.forEach((entity) => {
              if (selected.includes(entity.id)) {
                  const item = this.selection.selected.find((e) => e.id === entity.id);
                  this.selection.deselect(item!);
              }
          })
        : this.dataSource.data.forEach((entity) => {
              if (!selected.includes(entity.id)) this.selection.select(entity);
          });
  }
  
  doAction(eventName: string) {
    let self = this as any;
    return eventName in self && typeof self[eventName] === 'function' ? self[eventName]() : null;
  }
 
  public filterPaneClick(elem: HTMLElement, filterPane: ResourceListPane){
    elem.setAttribute('filterClickAttached','true')
    filterPane.isCollapsed ? this.filterFormGroup!.disable() : this.filterFormGroup!.enable();
    document.getElementById('resetButton')!.style.visibility = (filterPane.isCollapsed ? "hidden" : "visible");
    document.getElementById('submitButton')!.style.visibility = (filterPane.isCollapsed ? "hidden" : "visible");
    elem.addEventListener("click",()=>{
      filterPane.isCollapsed ? this.filterFormGroup!.disable() : this.filterFormGroup!.enable();
      sessionStorage.setItem("filterCollapsed", filterPane.isCollapsed ? "true" : "false");
      document.getElementById('resetButton')!.style.visibility = (filterPane.isCollapsed ? "hidden" : "visible");
      document.getElementById('submitButton')!.style.visibility = (filterPane.isCollapsed ? "hidden" : "visible");
    })
  }

  /**
   * Transforms the control value to a value on the datasource's filters mapping
   * @param name name of the control in the `filterFormGroup`
   * @param control 
   * @returns 
   */
  filterValue(name: string, control: AbstractControl): any {
    return control.value;
  }

  /**
   * Given the filter's key name, returns the value that should be set on the `filterFormGroup`'s control(s)
   * @param filterName the key name in the datasource's filters mapping 
   * @returns 
   */
  controlValue(filterName: string): any {
    return this.dataSource.filters[filterName];
  }

  /**
   * Given the filter's key name, returns the associated `filterFormGroup`'s control names
   * @param name the key name in the datasource's filters mapping 
   * @returns 
   */
  controlNamesForFilter(name: string): string[] {
    return [name];
  }

  /**
   * 
   * @param name the key name in the `FilterFormGroup` controls
   * @returns the key name in the datasource's filters mapping 
   */
  filterNameForControl(name: string): string {
    return name;
  }

  /**
   * Converts/transforms all the `filterFormGroup` control values to the datasource's filter values, 
   * creating a new filters mapping and setting it on the `dataSource`, which will trigger
   * the `dataSource` reload.
   */
  onFilterSubmit() {
    if (this.filterFormGroup) {
      // watch for submission, make Filters instance and pass to dataSource
      let filters: Filters = {};
      for (let key in this.filterFormGroup.controls) {
        let control = this.filterFormGroup.controls[key];
        if (control.valid) {
          let k = this.filterNameForControl(key);
          let value = this.filterValue(key, control);
          if (k && value || k && value === null) {
            filters[k] = value;
          } 
        }
      }
      // triggers loadData on dataSource
      this.dataSource.filters = filters;
    }
    this.selection.clear();
  }

  /**
   * Clears all the control values in `filterFormGroup`, as well as clearing
   * the filters mapping in `dataSource`. Will trigger the `dataSource` reload
   */
  clearFilterForm() {
    if (this.filterFormGroup) {  
      for (let key in this.filterFormGroup.controls) {
        let control = this.filterFormGroup.controls[key];
        control.setValue(undefined);
      }
      let filters: Filters = {};
      // triggers loadData on dataSource
      this.dataSource.filters = filters;
    }
    this.selection.clear();
  }
  
  ngAfterViewInit(): void {
    // delay to process on next event loop cycle
    // prevents the ExpressionChangedAfterItHasBeenCheckedError 
    setTimeout(() => { 
      
      if (this.filterFormGroup) {
        // synchronize the `filterFormGroup` control values to the `dataSource`'s filters mapping
        // so that they will be displayed to the user
        for (let key in this.dataSource.filters) {
          for (let controlName of this.controlNamesForFilter(key)) {
            let control = this.filterFormGroup.get(controlName);
            if (control) {
              control.setValue(this.controlValue(key));
            }
          }
        }
      }

      // doing this in ngAfterViewInit, since the MatSort and MatPaginator instances will be initialized
      // hook up the MatSort and MatPaginator with the `dataSource`
      this.dataSource.sort = this.sort || null;
      if (this.paginators) {
        this.dataSource.paginator = this.paginators.first;
        if (this.paginators.first != this.paginators.last) {
          this.dataSource.otherPaginator = this.paginators.last;
        }
      }
    });
  }
}
